import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub, TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { type GitHubPullRequestPayload, parseGitHubPullRequestPayload } from "./githubPullRequestSchema";
import { GitHubIssueMeta, safeGitHubUrl } from "./githubRenderUtils";

interface GitHubPullRequestRendererProps {
  block: StreamingBlock;
}

const REVIEW_STATE_CLASSES: Record<string, string> = {
  APPROVED: "text-green-600 dark:text-green-400",
  CHANGES_REQUESTED: "text-red-600 dark:text-red-400",
};

export const GitHubPullRequestRenderer = (props: GitHubPullRequestRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubPullRequestPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubPullRequestPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_pull_request_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="border-b border-primary-border px-3 py-2">
              <div class="flex items-baseline gap-2">
                <TbBrandGithub class="size-4 shrink-0 self-center text-primary-text" />
                <span class="shrink-0 font-mono text-xs text-muted-text">{data().reference}</span>
                <span class="truncate text-sm font-medium text-primary-text">{data().title}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">{formatIsoTimestamp(data().updatedAt)}</span>
              </div>
              <GitHubIssueMeta issue={data()} />
              <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                <Show when={data().merged}>
                  <span class="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    {rt.t("github_pull_request_card.merged")}
                  </span>
                </Show>
                <Show when={data().draft}>
                  <span class="shrink-0 rounded bg-inset px-1.5 py-0.5">{rt.t("github_pull_request_card.draft")}</span>
                </Show>
                <span class="truncate font-mono">
                  {rt.t("github_pull_request_card.branches", { head: data().headRef, base: data().baseRef })}
                </span>
                <span class="shrink-0">
                  {rt.t("github_pull_request_card.diff", {
                    additions: String(data().additions),
                    deletions: String(data().deletions),
                    files: String(data().changedFiles),
                  })}
                </span>
                <Show when={data().mergeableState}>
                  {(state) => (
                    <span class="shrink-0">{rt.t("github_pull_request_card.mergeable_state", { state: state() })}</span>
                  )}
                </Show>
              </div>
              <Show when={data().requestedReviewers.length > 0}>
                <div class="mt-1 text-xs text-muted-text">
                  {rt.t("github_pull_request_card.review_requested", {
                    reviewers: data().requestedReviewers.join(", "),
                  })}
                </div>
              </Show>
            </div>

            <Show when={data().body}>
              {(body) => (
                <div class="px-3 py-2">
                  <ScrollableFadeContainer maxHeight="200px">
                    <CompleteMarkdown markdown={body()} />
                  </ScrollableFadeContainer>
                </div>
              )}
            </Show>

            <Show when={data().reviews.length > 0}>
              <ul class="divide-y divide-primary-border border-t border-primary-border">
                <For each={data().reviews}>
                  {(review) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="flex min-w-0 items-baseline gap-1.5">
                          <span class="truncate text-sm font-medium text-primary-text">{review.reviewer}</span>
                          <span class={`shrink-0 text-xs ${REVIEW_STATE_CLASSES[review.state] ?? "text-muted-text"}`}>
                            {review.state}
                          </span>
                        </span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(review.submittedAt)}</span>
                      </div>
                      <Show when={review.body}>
                        {(body) => (
                          <div class="mt-1 line-clamp-3">
                            <CompleteMarkdown markdown={body()} />
                          </div>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </Show>

            <Show when={safeGitHubUrl(data().url)}>
              {(href) => (
                <div class="border-t border-primary-border px-3 py-2">
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                  >
                    {rt.t("github_pull_request_card.open_link")}
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                </div>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
